import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { RefreshCw, Search, Wallet, ArrowUpCircle, ArrowDownCircle, AlertCircle, CheckCircle2 } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface SaldoData {
  tipo_receita_despesa: string
  centro_resultado: string
  classificacao_receita_despesa: string
  valor_total_orcado: number
  valor_contratado: number
  valor_saldo_disponivel: number
  valor_movimentado_saida: number
  valor_movimentado_entrada: number
  valor_verba_adicional: number
  percentual_utilizado: number
  status_saldo: 'disponivel' | 'critico' | 'negativo'
}

export default function GestaoSaldoMovimentacoesPage() {
  const [data, setData] = useState<SaldoData[]>([])
  const [loading, setLoading] = useState(true)
  const [anoSelecionado, setAnoSelecionado] = useState<string>('')
  const [anos, setAnos] = useState<number[]>([])
  const [busca, setBusca] = useState('')
  const [filtroStatus, setFiltroStatus] = useState<string>('')
  const [centroSelecionado, setCentroSelecionado] = useState<string>('')

  useEffect(() => {
    fetchAnos()
  }, [])

  useEffect(() => {
    if (anoSelecionado) {
      fetchData()
    }
  }, [anoSelecionado])

  const fetchAnos = async () => {
    try {
      const { data: anosData, error } = await supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('ano')
        .order('ano', { ascending: false })

      if (error) throw error

      const anosUnicos = [...new Set(anosData?.map((d: any) => d.ano) || [])].sort((a, b) => b - a) as number[]
      setAnos(anosUnicos)
      if (anosUnicos.length > 0 && !anoSelecionado) {
        setAnoSelecionado(String(anosUnicos[0]))
      }
    } catch (error) {
      console.error('Erro ao buscar anos:', error)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('*')
        .eq('ano', parseInt(anoSelecionado))

      if (centroSelecionado) {
        query = query.eq('centro_resultado', centroSelecionado)
      }

      const { data: viewData, error } = await query

      if (error) throw error

      // Processar dados
      const dadosProcessados = (viewData || []).map((item) => {
        const valorOrcado = Number(item.valor_total_orcado || 0)
        const valorContratado = Number(item.valor_contratado || 0)
        const saldoDisponivel = Number(item.valor_saldo_disponivel || 0)
        const percentualUtilizado = valorOrcado > 0 ? (valorContratado / valorOrcado) * 100 : 0

        let statusSaldo: 'disponivel' | 'critico' | 'negativo' = 'disponivel'
        if (saldoDisponivel < 0) {
          statusSaldo = 'negativo'
        } else if (percentualUtilizado > 90 || saldoDisponivel < valorOrcado * 0.1) {
          statusSaldo = 'critico'
        }

        return {
          tipo_receita_despesa: item.tipo_receita_despesa || 'Sem tipo',
          centro_resultado: item.centro_resultado || 'Sem centro',
          classificacao_receita_despesa: item.classificacao_receita_despesa || 'Sem classificação',
          valor_total_orcado: valorOrcado,
          valor_contratado: valorContratado,
          valor_saldo_disponivel: saldoDisponivel,
          valor_movimentado_saida: Number(item.valor_movimentado_saida || 0),
          valor_movimentado_entrada: Number(item.valor_movimentado_entrada || 0),
          valor_verba_adicional: Number(item.valor_verba_adicional || 0),
          percentual_utilizado: percentualUtilizado,
          status_saldo: statusSaldo,
        }
      })

      // Ordenar por saldo disponível (menores primeiro para destacar problemas)
      dadosProcessados.sort((a, b) => a.valor_saldo_disponivel - b.valor_saldo_disponivel)

      setData(dadosProcessados)
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  // Filtrar dados
  let dadosFiltrados = data.filter((item) => {
    const matchBusca =
      item.tipo_receita_despesa.toLowerCase().includes(busca.toLowerCase()) ||
      item.centro_resultado.toLowerCase().includes(busca.toLowerCase()) ||
      item.classificacao_receita_despesa.toLowerCase().includes(busca.toLowerCase())

    const matchStatus = !filtroStatus || filtroStatus === '__all__' || item.status_saldo === filtroStatus

    return matchBusca && matchStatus
  })

  // Estatísticas
  const totalItens = dadosFiltrados.length
  const itensNegativos = dadosFiltrados.filter((item) => item.status_saldo === 'negativo').length
  const itensCriticos = dadosFiltrados.filter((item) => item.status_saldo === 'critico').length
  const itensDisponiveis = dadosFiltrados.filter((item) => item.status_saldo === 'disponivel').length

  const totalSaldoDisponivel = dadosFiltrados.reduce((sum, item) => sum + item.valor_saldo_disponivel, 0)
  const totalMovimentadoEntrada = dadosFiltrados.reduce((sum, item) => sum + item.valor_movimentado_entrada, 0)
  const totalMovimentadoSaida = dadosFiltrados.reduce((sum, item) => sum + item.valor_movimentado_saida, 0)

  // Lista de centros únicos
  const centrosUnicos = [...new Set(data.map((item) => item.centro_resultado))].sort()

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'negativo':
        return <Badge variant="destructive">Saldo Negativo</Badge>
      case 'critico':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-700">Crítico</Badge>
      default:
        return <Badge variant="outline" className="border-green-500 text-green-700">Disponível</Badge>
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Gestão de Saldo e Movimentações</h2>
          <p className="text-muted-foreground text-sm">
            Acompanhe saldos disponíveis, movimentações e alertas por tipo de despesa
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={anoSelecionado} onValueChange={setAnoSelecionado}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecione o ano" />
            </SelectTrigger>
            <SelectContent>
              {anos.map((ano) => (
                <SelectItem key={ano} value={String(ano)}>
                  {ano}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPIs de Status */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Itens</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalItens}</div>
            <p className="text-xs text-muted-foreground">Tipos de despesa analisados</p>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-800">Saldos Negativos</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{itensNegativos}</div>
            <p className="text-xs text-red-600">Requer atenção imediata</p>
          </CardContent>
        </Card>

        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-800">Saldos Críticos</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{itensCriticos}</div>
            <p className="text-xs text-yellow-600">Acima de 90% utilizado</p>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-800">Saldos Disponíveis</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{itensDisponiveis}</div>
            <p className="text-xs text-green-600">Com saldo adequado</p>
          </CardContent>
        </Card>
      </div>

      {/* Resumo de Movimentações */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo de Movimentações</CardTitle>
          <CardDescription>Total de entradas, saídas e saldo líquido</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border rounded-lg bg-green-50">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">Movimentações de Entrada</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(totalMovimentadoEntrada)}</div>
            </div>
            <div className="p-4 border rounded-lg bg-red-50">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDownCircle className="h-5 w-5 text-red-600" />
                <span className="text-sm font-medium text-red-800">Movimentações de Saída</span>
              </div>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(totalMovimentadoSaida)}</div>
            </div>
            <div className="p-4 border rounded-lg bg-blue-50">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Saldo Total Disponível</span>
              </div>
              <div className={`text-2xl font-bold ${totalSaldoDisponivel < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                {formatCurrency(totalSaldoDisponivel)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tipo, centro ou classificação..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Status do Saldo</label>
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os status</SelectItem>
                  <SelectItem value="negativo">Saldo Negativo</SelectItem>
                  <SelectItem value="critico">Crítico</SelectItem>
                  <SelectItem value="disponivel">Disponível</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Centro de Custo</label>
              <Select
                value={centroSelecionado}
                onValueChange={(val) => {
                  setCentroSelecionado(val === '__all__' ? '' : val)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os centros" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os centros</SelectItem>
                  {centrosUnicos.map((centro) => (
                    <SelectItem key={centro} value={centro}>
                      {centro}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela detalhada */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento de Saldos e Movimentações</CardTitle>
          <CardDescription>
            Análise completa por tipo de despesa, centro de custo e classificação
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : dadosFiltrados.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum registro encontrado com os filtros selecionados.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Tipo de Despesa</TableHead>
                    <TableHead>Centro de Custo</TableHead>
                    <TableHead>Classificação</TableHead>
                    <TableHead className="text-right">Orçado</TableHead>
                    <TableHead className="text-right">Contratado</TableHead>
                    <TableHead className="text-right">% Utilizado</TableHead>
                    <TableHead className="text-right">Saldo Disponível</TableHead>
                    <TableHead className="text-right">Mov. Entrada</TableHead>
                    <TableHead className="text-right">Mov. Saída</TableHead>
                    <TableHead className="text-right">Verba Adicional</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dadosFiltrados.map((item, idx) => (
                    <TableRow
                      key={idx}
                      className={item.status_saldo === 'negativo' ? 'bg-red-50' : item.status_saldo === 'critico' ? 'bg-yellow-50' : ''}
                    >
                      <TableCell>{getStatusBadge(item.status_saldo)}</TableCell>
                      <TableCell className="font-medium">{item.tipo_receita_despesa}</TableCell>
                      <TableCell>{item.centro_resultado}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.classificacao_receita_despesa}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        {formatCurrency(item.valor_total_orcado)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-orange-600">
                        {formatCurrency(item.valor_contratado)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-muted rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                item.percentual_utilizado > 90
                                  ? 'bg-red-500'
                                  : item.percentual_utilizado > 70
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(item.percentual_utilizado, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{item.percentual_utilizado.toFixed(1)}%</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={`text-right font-bold ${
                          item.valor_saldo_disponivel < 0 ? 'text-red-600' : 'text-blue-600'
                        }`}
                      >
                        {formatCurrency(item.valor_saldo_disponivel)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(item.valor_movimentado_entrada)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatCurrency(item.valor_movimentado_saida)}
                      </TableCell>
                      <TableCell className="text-right text-blue-600">
                        {formatCurrency(item.valor_verba_adicional)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

